/**
 * Web Bluetooth thermal-label printing (Android/Chrome only — iOS browsers do
 * not support Web Bluetooth).
 *
 * Targets the Jiabo/Gprinter GP-3120TUC (203 dpi). Default command language is
 * TSPL (native for this label printer); ESC/POS raster is also supported.
 *
 * The exact BLE write characteristic is firmware-specific, so we:
 *   1. include a broad list of common thermal-printer service UUIDs (+ any
 *      admin-configured one) in optionalServices, and
 *   2. auto-discover the first writable characteristic after connecting,
 *      unless an explicit write UUID is configured.
 */

const MM_PER_IN = 25.4;

// Common BLE services exposed by cheap thermal/label printers.
const COMMON_SERVICES = [
  "000018f0-0000-1000-8000-00805f9b34fb", // common ESC/POS printer service
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "0000fee7-0000-1000-8000-00805f9b34fb",
  "0000ff80-0000-1000-8000-00805f9b34fb",
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // Microchip/ISSC transparent UART (very common)
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e", // Nordic UART
];

export function isBluetoothSupported() {
  return typeof navigator !== "undefined" && !!navigator.bluetooth;
}

let _device = null;
let _characteristic = null;

function norm(uuid) {
  return (uuid || "").trim().toLowerCase() || null;
}

let _cfg = {};

function _bind(device, cfg = {}) {
  _device = device;
  _cfg = cfg;
  _device.addEventListener("gattserverdisconnected", () => {
    _characteristic = null;
    // Best-effort silent reconnect if the link drops mid-session (printer still permitted).
    setTimeout(() => { _silentReconnect(); }, 1000);
  });
}

async function _silentReconnect() {
  if (!_device || !_device.gatt) return;
  try {
    const server = await _device.gatt.connect();
    await _resolveCharacteristic(server, _cfg);
  } catch { _characteristic = null; }
}

async function _resolveCharacteristic(server, cfg) {
  const svc = norm(cfg.ble_service_uuid);
  const writeUuid = norm(cfg.ble_write_uuid);
  // Explicit UUIDs win when provided.
  if (svc && writeUuid) {
    const service = await server.getPrimaryService(svc);
    _characteristic = await service.getCharacteristic(writeUuid);
    return;
  }
  // Otherwise discover the first writable characteristic across services.
  const services = await server.getPrimaryServices();
  for (const service of services) {
    let chars = [];
    try { chars = await service.getCharacteristics(); } catch { continue; }
    for (const ch of chars) {
      if (ch.properties.write || ch.properties.writeWithoutResponse) {
        _characteristic = ch;
        return;
      }
    }
  }
  throw new Error("Connected, but no writable channel was found. In the admin Printer settings, paste the Service + Write UUID from the nRF Connect app.");
}

/** Silently reconnect to a previously-paired printer (no chooser). Best-effort;
 *  returns true only when a writable characteristic is ready. */
export async function reconnectPrinter(cfg = {}) {
  if (!isBluetoothSupported() || typeof navigator.bluetooth.getDevices !== "function") return false;
  let devices = [];
  try { devices = await navigator.bluetooth.getDevices(); } catch { return false; }
  if (!devices.length) return false;
  let savedId = null;
  try { savedId = localStorage.getItem("vb_bt_printer_id"); } catch { /* ignore */ }
  const dev = devices.find((d) => d.id === savedId) || devices[0];
  if (!dev) return false;
  try {
    _bind(dev, cfg);
    const server = await dev.gatt.connect();
    await _resolveCharacteristic(server, cfg);
    return isPrinterConnected();
  } catch { _characteristic = null; return false; }
}

/** Request + connect to the printer (shows the chooser) and resolve a writable
 *  characteristic. Remembers the chosen device for silent reconnect next time. */
export async function connectPrinter(cfg = {}) {
  if (!isBluetoothSupported()) {
    throw new Error("This device/browser doesn't support Bluetooth printing. Use Chrome on Android, or the standard Print button.");
  }
  const svc = norm(cfg.ble_service_uuid);
  const optionalServices = Array.from(new Set([...(svc ? [svc] : []), ...COMMON_SERVICES]));
  const device = await navigator.bluetooth.requestDevice({ acceptAllDevices: true, optionalServices });
  _bind(device, cfg);
  try { localStorage.setItem("vb_bt_printer_id", device.id); } catch { /* ignore */ }
  const server = await device.gatt.connect();
  await _resolveCharacteristic(server, cfg);
  return device.name || "printer";
}

export function isPrinterConnected() {
  return !!(_device && _device.gatt && _device.gatt.connected && _characteristic);
}

/** Name of the currently-bound printer, if any. */
export function getPrinterName() {
  return (_device && _device.name) || null;
}

/** True once a printer has been paired at least once on this device/browser. */
export function hasRememberedPrinter() {
  try { return !!localStorage.getItem("vb_bt_printer_id"); } catch { return false; }
}

export function disconnectPrinter() {
  try { if (_device?.gatt?.connected) _device.gatt.disconnect(); } catch (e) { console.debug(e); }
  _device = null; _characteristic = null;
}

async function writeChunks(bytes) {
  if (!_characteristic) throw new Error("Printer not connected");
  const useNoResponse = _characteristic.properties.writeWithoutResponse;
  const CHUNK = 180;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.slice(i, i + CHUNK);
    if (useNoResponse && _characteristic.writeValueWithoutResponse) {
      await _characteristic.writeValueWithoutResponse(slice);
    } else {
      await _characteristic.writeValue(slice);
    }
    await new Promise((r) => setTimeout(r, 8));
  }
}

/** Threshold a canvas to a 1bpp, row-padded buffer. Returns {widthBytes, height, dark:Uint8Array bits where 1 = black}. */
function canvasToMono(canvas) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const img = ctx.getImageData(0, 0, width, height).data;
  const widthBytes = Math.ceil(width / 8);
  const rows = new Uint8Array(widthBytes * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const a = img[idx + 3];
      const lum = (img[idx] * 0.299 + img[idx + 1] * 0.587 + img[idx + 2] * 0.114);
      const black = a > 32 && lum < 128;
      if (black) rows[y * widthBytes + (x >> 3)] |= (0x80 >> (x & 7));
    }
  }
  return { widthBytes, height, bits: rows };
}

const enc = (s) => new TextEncoder().encode(s);

function concat(parts) {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) { out.set(p, off); off += p.length; }
  return out;
}

/** Build a TSPL job: SIZE/GAP + BITMAP (black=0 per TSPL) + PRINT. */
function buildTSPL(canvas, { widthMm, heightMm, gapMm = 2, copies = 1 }) {
  const { widthBytes, height, bits } = canvasToMono(canvas);
  // TSPL BITMAP: bit 0 = dot ON (black). Our `bits` has 1 = black → invert.
  const data = new Uint8Array(bits.length);
  for (let i = 0; i < bits.length; i++) data[i] = ~bits[i] & 0xff;
  const n = Math.max(1, Math.min(parseInt(copies, 10) || 1, 10000));
  const header = enc(
    `SIZE ${widthMm} mm,${heightMm} mm\r\n` +
    `GAP ${gapMm} mm,0 mm\r\n` +
    `DIRECTION 1\r\n` +
    `CLS\r\n` +
    `BITMAP 0,0,${widthBytes},${height},0,`
  );
  // PRINT m,n → m label sets, n copies of each. One bitmap, n physical labels.
  return concat([header, data, enc(`\r\nPRINT 1,${n}\r\n`)]);
}

/** Build an ESC/POS raster job (GS v 0). bit 1 = black. */
function buildESCPOS(canvas, copies = 1) {
  const { widthBytes, height, bits } = canvasToMono(canvas);
  const xL = widthBytes & 0xff, xH = (widthBytes >> 8) & 0xff;
  const yL = height & 0xff, yH = (height >> 8) & 0xff;
  const init = new Uint8Array([0x1b, 0x40]); // ESC @
  const raster = new Uint8Array([0x1d, 0x76, 0x30, 0x00, xL, xH, yL, yH]);
  const feed = new Uint8Array([0x1b, 0x64, 0x03]); // feed 3 lines
  const n = Math.max(1, Math.min(parseInt(copies, 10) || 1, 10000));
  const one = concat([raster, bits, feed]);
  const parts = [init];
  for (let i = 0; i < n; i++) parts.push(one);
  return concat(parts);
}

/** Render the given canvas to the connected printer (n copies) using the configured command set. */
export async function printCanvas(canvas, cfg = {}, copies = 1) {
  const job = (cfg.printer_command || "tspl") === "escpos"
    ? buildESCPOS(canvas, copies)
    : buildTSPL(canvas, {
        widthMm: cfg.label_width_mm || 80,
        heightMm: cfg.label_height_mm || 50,
        gapMm: cfg.printer_gap_mm ?? 2,
        copies,
      });
  await writeChunks(job);
}
